import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CategoryTree from '../../components/categories/CategoryTree';
import categoryService from '../../services/categoryService';
import './CategoryListPage.css';

function CategoryListPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all'); // all, active, inactive
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name, hasProducts, hasChildren }

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    filterCategories();
  }, [categories, searchTerm, filterActive]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categoryService.getCategories();
      setCategories(data);
    } catch (err) {
      setError(err.message || 'Failed to load categories');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterCategories = () => {
    let filtered = [...categories];

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(cat =>
        cat.name.toLowerCase().includes(term) ||
        cat.slug.toLowerCase().includes(term) ||
        (cat.description && cat.description.toLowerCase().includes(term))
      );
    }

    // Apply active filter
    if (filterActive === 'active') {
      filtered = filtered.filter(cat => cat.isActive);
    } else if (filterActive === 'inactive') {
      filtered = filtered.filter(cat => !cat.isActive);
    }

    setFilteredCategories(filtered);
  };

  const handleEdit = (categoryId) => {
    navigate(`/categories/edit/${categoryId}`);
  };

  const handleDeleteClick = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;

    // Check if category has products
    const hasProducts = category.productCount > 0;

    // Check if category has children
    const hasChildren = categories.some(c => c.parent?.id === categoryId);

    setDeleteConfirm({
      id: categoryId,
      name: category.name,
      hasProducts,
      hasChildren,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    // Safety check - prevent deletion if has products or children
    if (deleteConfirm.hasProducts || deleteConfirm.hasChildren) {
      alert(
        `Cannot delete this category:\n\n` +
        (deleteConfirm.hasProducts ? `- It has ${deleteConfirm.hasProducts} product(s) assigned\n` : '') +
        (deleteConfirm.hasChildren ? `- It has subcategories\n` : '') +
        `\nPlease remove all products and subcategories first.`
      );
      setDeleteConfirm(null);
      return;
    }

    try {
      await categoryService.deleteCategory(deleteConfirm.id);
      setCategories(categories.filter(c => c.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      alert(`Failed to delete category: ${err.message}`);
      console.error('Error deleting category:', err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const handleCreateNew = () => {
    navigate('/categories/new');
  };

  if (loading) {
    return (
      <div className="category-list-page">
        <div className="page-loading">
          <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p>Loading categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="category-list-page">
        <div className="page-error">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <h3>Error Loading Categories</h3>
          <p>{error}</p>
          <button onClick={fetchCategories} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="category-list-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Categories</h1>
          <p className="header-subtitle">
            Manage your product categories and subcategories
          </p>
        </div>
        <button onClick={handleCreateNew} className="btn btn-primary">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Category
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Categories</div>
          <div className="stat-value">{categories.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value">{categories.filter(c => c.isActive).length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Inactive</div>
          <div className="stat-value">{categories.filter(c => !c.isActive).length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Root Categories</div>
          <div className="stat-value">{categories.filter(c => !c.parent).length}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search categories by name, slug, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="clear-search"
              aria-label="Clear search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="filter-tabs">
          <button
            className={`filter-tab ${filterActive === 'all' ? 'active' : ''}`}
            onClick={() => setFilterActive('all')}
          >
            All
          </button>
          <button
            className={`filter-tab ${filterActive === 'active' ? 'active' : ''}`}
            onClick={() => setFilterActive('active')}
          >
            Active
          </button>
          <button
            className={`filter-tab ${filterActive === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilterActive('inactive')}
          >
            Inactive
          </button>
        </div>
      </div>

      {/* Results Count */}
      {searchTerm && (
        <div className="results-info">
          Found {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'}
          {searchTerm && ` matching "${searchTerm}"`}
        </div>
      )}

      {/* Category Tree */}
      <div className="content-section">
        <CategoryTree
          categories={filteredCategories}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Category</h3>
              <button onClick={handleDeleteCancel} className="modal-close" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {deleteConfirm.hasProducts || deleteConfirm.hasChildren ? (
                <>
                  <div className="warning-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                  <p className="modal-text">
                    Cannot delete <strong>{deleteConfirm.name}</strong>:
                  </p>
                  <ul className="warning-list">
                    {deleteConfirm.hasProducts && (
                      <li>This category has {deleteConfirm.hasProducts} product(s) assigned</li>
                    )}
                    {deleteConfirm.hasChildren && (
                      <li>This category has subcategories</li>
                    )}
                  </ul>
                  <p className="modal-hint">
                    Please remove all products and subcategories before deleting this category.
                  </p>
                </>
              ) : (
                <>
                  <div className="danger-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                  <p className="modal-text">
                    Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
                  </p>
                  <p className="modal-hint">
                    This action cannot be undone.
                  </p>
                </>
              )}
            </div>
            <div className="modal-actions">
              <button onClick={handleDeleteCancel} className="btn btn-secondary">
                Cancel
              </button>
              {!deleteConfirm.hasProducts && !deleteConfirm.hasChildren && (
                <button onClick={handleDeleteConfirm} className="btn btn-danger">
                  Delete Category
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryListPage;
